/**
 * Text Constants
 * All UI texts centralized for easy management and future i18n support
 */

export const TEXTS = {
  // ============ App General ============
  APP: {
    NAME: "DevCraft Contracts",
    TAGLINE: "חתימה דיגיטלית מאובטחת",
    COMPANY: "DevCraft IL",
  },

  // ============ Common ============
  COMMON: {
    LOADING: "טוען...",
    ERROR: "שגיאה",
    TRY_AGAIN: "נסה שוב",
    BACK: "חזור",
    SUBMIT: "שלח",
    CLOSE: "סגור",
    YES: "כן",
    NO: "לא",
    REQUIRED_FIELD: "*",
    NETWORK_ERROR: "שגיאת רשת. אנא בדוק את החיבור שלך.",
    UNEXPECTED_ERROR: "אירעה שגיאה בלתי צפויה",
  },

  // ============ Navigation ============
  NAV: {
    HOME: "דף הבית",
    MY_DOCUMENTS: "המסמכים שלי",
    BACK: "חזור",
  },

  // ============ Not Found Page ============
  NOT_FOUND: {
    CODE: "404",
    TITLE: "הדף לא נמצא",
    MESSAGE: "הקישור שניסית לגשת אליו אינו קיים או שפג תוקפו.",
    GO_HOME: "חזור לדף הבית",
  },

  // ============ Phone Verification ============
  VERIFICATION: {
    GREETING: "שלום",
    PHONE_PROMPT: "לצפייה בחוזה, אנא הזן את מספר הטלפון שלך לאימות",
    PHONE_REGISTERED: "מספר הטלפון הרשום:",
    PHONE_LABEL: "מספר טלפון",
    PHONE_PLACEHOLDER: "050-000-0000",
    PHONE_PREFIX: "+972",
    SEND_OTP: "שלח קוד אימות",

    OTP_TITLE: "הזן קוד אימות",
    OTP_SENT_TO: "שלחנו קוד בן 6 ספרות למספר",
    OTP_VERIFY: "אמת והמשך",
    OTP_RESEND: "לא קיבלת? שלח שוב",
    OTP_RESEND_COUNTDOWN: "שלח שוב בעוד {seconds} שניות",
    OTP_DEV_MODE: "Development Mode:",
    OTP_CODE_LABEL: "OTP Code:",

    ERRORS: {
      INVALID_PHONE: "אנא הזן מספר טלפון תקין",
      SEND_FAILED: "אירעה שגיאה בשליחת הקוד",
      INVALID_OTP: "קוד שגוי, נסה שוב",
      INCOMPLETE_OTP: "אנא הזן את כל 6 הספרות",
    },
  },

  // ============ Contract View ============
  CONTRACT: {
    TITLE: "חוזה שירות",
    GREETING: "שלום",
    SCROLL_HINT: "לקריאה מלאה גלול למטה, ואז חתום בתחתית העמוד",

    SUMMARY: {
      SERVICE: "שירות",
      PRICE: "מחיר",
      START_DATE: "תאריך התחלה",
    },

    CHECKBOXES: {
      TITLE: "אישורים נדרשים",
      OPTIONAL_TITLE: "אופציונלי",
    },

    SIGNATURE: {
      TITLE: "חתימה דיגיטלית",
      PLACEHOLDER: "חתום כאן",
      HINT: "השתמש באצבע או בעכבר לחתימה",
      CLEAR: "נקה חתימה",
    },

    SUBMIT: {
      BUTTON: "חתום ושלח",
      SIGNING: "חותם...",
    },

    VALIDATION: {
      CHECKBOXES_REQUIRED: "יש לסמן את כל האישורים הנדרשים",
      SIGNATURE_REQUIRED: "יש לחתום לפני השליחה",
    },

    ERRORS: {
      SIGN_FAILED: "אירעה שגיאה בחתימה על החוזה",
      LOAD_FAILED: "אירעה שגיאה בטעינת החוזה",
      NOT_FOUND: "החוזה לא נמצא",
    },
  },

  // ============ Success Screen ============
  SUCCESS: {
    TITLE: "החוזה נחתם בהצלחה!",
    THANK_YOU: "תודה {name}, החתימה שלך התקבלה",

    DETAILS: {
      SIGNED_AT: "תאריך חתימה:",
      CONTRACT_ID: "מזהה חוזה:",
      VERIFICATION_HASH: "Hash אימות:",
    },

    EMAIL_NOTICE:
      "📧 העתק של החוזה החתום נשלח אליך במייל.\nשמור מסמך זה לרישומיך.",
    CAN_CLOSE: "ניתן לסגור חלון זה.",
  },

  // ============ Expired Screen ============
  EXPIRED: {
    TITLE: "הקישור פג תוקף",
    MESSAGE: "הקישור לחוזה זה פג תוקפו.",

    WHAT_TO_DO: {
      TITLE: "מה ניתן לעשות?",
      CONTACT_PROVIDER: "פנה לספק השירות לקבלת קישור חדש",
      CHECK_MESSAGES: "בדוק את תיבת ההודעות שלך לקישור עדכני",
    },

    NOTE: "קישורים לחוזים תקפים ל-72 שעות מרגע השליחה.",
  },

  // ============ Already Signed ============
  ALREADY_SIGNED: {
    TITLE: "החוזה כבר נחתם",
    MESSAGE: "חוזה זה כבר נחתם בעבר. לא ניתן לחתום עליו שוב.",
  },

  // ============ Home Page ============
  HOME: {
    TITLE: "מערכת חתימה דיגיטלית",
    DESCRIPTION: "מערכת זו מאפשרת חתימה דיגיטלית מאובטחת על חוזים ומסמכים.",
    HOW_IT_WORKS: "איך זה עובד?",
    STEP_1: "תקבל קישור ייחודי באמצעות SMS",
    STEP_2: "תאמת את זהותך באמצעות קוד חד-פעמי",
    STEP_3: "תקרא את המסמך ותחתום דיגיטלית",
    FOOTER: "© DevCraft Contracts - חתימה דיגיטלית מאובטחת",
    LOOKUP_BUTTON: "יש לי מסמכים לחתימה",
  },

  // ============ Document Lookup ============
  LOOKUP: {
    TITLE: "צפייה במסמכים שלי",
    SUBTITLE: "הזן את מספר הטלפון שלך לצפייה במסמכים הממתינים לחתימתך",
    PHONE_LABEL: "מספר טלפון",
    PHONE_PLACEHOLDER: "050-000-0000",
    PHONE_PREFIX: "+972",
    SEND_OTP: "שלח קוד אימות",

    OTP_TITLE: "הזן קוד אימות",
    OTP_SENT_TO: "שלחנו קוד בן 6 ספרות למספר",
    OTP_VERIFY: "אמת והמשך",
    OTP_RESEND: "לא קיבלת? שלח שוב",
    OTP_RESEND_COUNTDOWN: "שלח שוב בעוד {seconds} שניות",
    OTP_DEV_MODE: "Development Mode:",
    OTP_CODE_LABEL: "OTP Code:",

    CONTRACTS_TITLE: "המסמכים שלי",
    CONTRACTS_SUBTITLE: "נמצאו {count} מסמכים הממתינים לחתימתך",
    NO_CONTRACTS: "לא נמצאו מסמכים זמינים",
    CONTRACT_CARD: {
      TEMPLATE: "סוג מסמך",
      STATUS: "סטטוס",
      CREATED: "תאריך יצירה",
      EXPIRES: "תוקף עד",
      SERVICE: "שירות",
      PRICE: "מחיר",
      SIGN_NOW: "לחתימה",
    },

    STATUS: {
      PENDING: "ממתין לחתימה",
      VIEWED: "נצפה",
    },

    ERRORS: {
      INVALID_PHONE: "אנא הזן מספר טלפון תקין",
      SEND_FAILED: "אירעה שגיאה בשליחת הקוד",
      INVALID_OTP: "קוד שגוי, נסה שוב",
      INCOMPLETE_OTP: "אנא הזן את כל 6 הספרות",
      NO_CONTRACTS: "לא נמצאו מסמכים זמינים עבור מספר טלפון זה",
      LOAD_FAILED: "אירעה שגיאה בטעינת המסמכים",
      TOKEN_FAILED: "אירעה שגיאה בגישה למסמך",
    },

    CLOSE: "סגור",
    BACK: "חזור",
  },
};

/**
 * Helper function to replace placeholders in text
 * @param {string} text - Text with placeholders like {name}
 * @param {Object} values - Object with values to replace
 * @returns {string} Text with replaced values
 */
export const formatText = (text, values = {}) => {
  let result = text;
  Object.entries(values).forEach(([key, value]) => {
    result = result.replace(new RegExp(`\\{${key}\\}`, "g"), value);
  });
  return result;
};

export default TEXTS;
